import { useCallback, useMemo, useRef, useState } from 'react';
import { Breadcrumb } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { transformFiltersV2 } from 'utils/helperMethods';

import CreatePlanModal, { type CreatedPlanInfo } from '../components/CreatePlanModal';
import { type PlanRecord } from '../types/plan';
import PlansIndexTable, { type PlansIndexTableHandle } from './PlansIndexTable';
import ComparisonReportsTable from '../ComparisonReports/ComparisonReportsTable';
import PlanIndexToolbar from './PlanIndexToolbar';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { acquireEditLock, commitEditLockSnapshot } from '../PlanDetail/EditSession/editLockActions';
import {
    TPO_PLAN_INDEX_BREADCRUMB,
    TPO_PLAN_INDEX_BREADCRUMB_ROOT_LINK,
    TPO_PLAN_INDEX_PAGE,
} from '../config/testIds';
import { usePlanIndexUrlState } from './hooks/ui/usePlanIndexUrlState';
import { useQueryClient } from '@tanstack/react-query';
import { TPOGEN_CREATE_PLAN_CLICKED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';

dayjs.extend(relativeTime);

export type PlanIndexTab = 'plans' | 'comparison-reports';

const PlanIndex = () => {
    const navigate = useNavigate();
    const { state: locationState, search } = useLocation();
    const tpoGenConfig = useTpoGenConfig();
    const { planIndexFilterKeys, editLock, scenarioComparisonConfig } = tpoGenConfig;
    const { comparisonReportColumns, comparisonReportFilterKeys } = scenarioComparisonConfig;
    const queryClient = useQueryClient();
    const plansTableRef = useRef<PlansIndexTableHandle>(null);
    const initialTab = (locationState as { activeTab?: PlanIndexTab })?.activeTab;
    const [createPlanModalOpen, setCreatePlanModalOpen] = useState(false);
    const [createPlanGroupDetails, setCreatePlanGroupDetails] = useState<PlanRecord | null>(null);
    const [editPlanDetails, setEditPlanDetails] = useState<PlanRecord | null>(null);

    const {
        activeTab,
        setActiveTab,
        searchKeyword,
        setSearchKeyword,
        showMyPlans,
        setShowMyPlans,
        selectedColumns,
        setSelectedColumns,
        selectedComparisonColumns,
        setSelectedComparisonColumns,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        setAppliedFilters,
        resetPlanIndexFilters,
    } = usePlanIndexUrlState(initialTab);

    const filters = useMemo(
        () => transformFiltersV2(applyFilterObject, planIndexFilterKeys),
        [applyFilterObject, planIndexFilterKeys],
    );

    const comparisonFilters = useMemo(
        () => transformFiltersV2(applyFilterObject, comparisonReportFilterKeys),
        [applyFilterObject, comparisonReportFilterKeys],
    );

    const onCreateNewPlanClick = useCallback(() => {
        trackTpoGenEvent(TPOGEN_CREATE_PLAN_CLICKED, TPOGEN_PAGE_NAMES.PLANS_LIST, { entry_point: 'plans' });
        setEditPlanDetails(null);
        setCreatePlanGroupDetails(null);
        setCreatePlanModalOpen(true);
    }, []);

    const onCreateNewReportClick = useCallback(() => {
        navigate('/tpo-gen/scenario-comparison/new', { state: { createNew: true, fromSearch: search } });
    }, [navigate, search]);

    const onCreatePlanSuccess = useCallback(
        (createdPlan?: CreatedPlanInfo) => {
            if (createdPlan) {
                // A freshly created plan has no prior editors, so it should land in
                // edit mode without the user having to ask for it explicitly. Navigation
                // does not wait on this: the plan detail page mounts immediately and its
                // own lock query picks up the cache write as soon as acquiring resolves.
                // `pendingEditAcquire` tells the detail page to show a loading state
                // instead of the read-only view for the (usually brief) time in between.
                if (editLock.enabled) {
                    acquireEditLock(createdPlan.planId, createdPlan.planName)
                        .then(({ snapshot, acquired }) => {
                            if (acquired) return commitEditLockSnapshot(queryClient, createdPlan.planId, snapshot);
                        })
                        .catch(() => {
                            // Non-fatal: the plan still opens, just read-only until the
                            // user clicks "Edit plan" themselves.
                        });
                }
                navigate(`/tpo-gen/plan/${createdPlan.planId}`, {
                    state: {
                        planId: createdPlan.planId,
                        planName: createdPlan.planName,
                        pendingEditAcquire: editLock.enabled,
                    },
                });
                return;
            }
            plansTableRef.current?.refreshExpandedGroups();
        },
        [navigate, editLock.enabled, queryClient],
    );

    return (
        <div data-testid={TPO_PLAN_INDEX_PAGE} className="flex flex-col" style={{ minHeight: 'calc(100vh - 130px)' }}>
            <div className="mb-3" data-testid={TPO_PLAN_INDEX_BREADCRUMB}>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <span data-testid={TPO_PLAN_INDEX_BREADCRUMB_ROOT_LINK}>
                            {tpoGenConfig.labels.common.breadcrumbRoot}
                        </span>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        {activeTab === 'plans'
                            ? tpoGenConfig.labels.planIndex.breadcrumb
                            : tpoGenConfig.labels.comparisonReport.breadcrumb}
                    </Breadcrumb.Item>
                </Breadcrumb>
            </div>
            <div className="flex flex-col px-[1.5rem] bg-[#FFF] py-[1.75rem] rounded-[0.25rem] border-[1] border-[#EAEAEC] flex-1">
                <CreatePlanModal
                    open={createPlanModalOpen}
                    onCancel={() => {
                        setCreatePlanModalOpen(false);
                        setCreatePlanGroupDetails(null);
                        setEditPlanDetails(null);
                    }}
                    onSuccessHandler={onCreatePlanSuccess}
                    createMode={createPlanGroupDetails ? 'planning-group-create' : 'plan-create'}
                    planningGroupDetails={createPlanGroupDetails}
                    editPlanDetails={editPlanDetails}
                />
                <PlanIndexToolbar
                    key={activeTab}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    searchKeyword={searchKeyword}
                    setSearchKeyword={setSearchKeyword}
                    showMyPlans={showMyPlans}
                    setShowMyPlans={setShowMyPlans}
                    selectedColumns={selectedColumns}
                    setSelectedColumns={setSelectedColumns}
                    selectedComparisonColumns={selectedComparisonColumns}
                    setSelectedComparisonColumns={setSelectedComparisonColumns}
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    setAppliedFilters={setAppliedFilters}
                    resetPlanIndexFilters={resetPlanIndexFilters}
                    onCreateNewPlanClick={onCreateNewPlanClick}
                    onCreateNewReportClick={onCreateNewReportClick}
                />

                {activeTab === 'plans' ? (
                    <PlansIndexTable
                        ref={plansTableRef}
                        searchKeyword={searchKeyword}
                        selectedColumns={selectedColumns}
                        showMyPlans={showMyPlans}
                        filters={filters}
                        setCreatePlanGroupDetails={setCreatePlanGroupDetails}
                        setCreatePlanModalOpen={setCreatePlanModalOpen}
                        setEditPlanDetails={setEditPlanDetails}
                    />
                ) : (
                    <ComparisonReportsTable
                        searchKeyword={searchKeyword}
                        showMyReports={showMyPlans}
                        selectedColumns={selectedComparisonColumns}
                        filters={comparisonFilters}
                    />
                )}
            </div>
        </div>
    );
};

export default PlanIndex;
